import { useIntl } from "react-intl";
import { Asset } from "../../../models/asset";
import { Scheduling } from "../../../models/schedule";
import { SignedRevision } from "../../../models/signedRevision";
import useAssets from "../../../hooks/useAssets";
import { getRevisionByIndex, getChassisData, ChassisRevision } from "../../../data/chassisReviewData";

interface Blockchain {
  transactionDate: string;
  transactionHash: string;
}

interface IPeriodicReviewsPdfProps {
  asset: Asset | null;
  blockchainData: (scheduleId: string) => SignedRevision | undefined;
  order: string;
  paginateSchedulings: (schedulings: Scheduling[]) => Scheduling[][];
  userRole?: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP';
}

const PeriodicReviewsPdf = ({
  asset,
  blockchainData,
  order,
  paginateSchedulings,
  userRole,
}: IPeriodicReviewsPdfProps) => {
  const intl = useIntl();
  const { getAssetById } = useAssets();
  const summaryAsset = asset?.id ? getAssetById(asset.id) : undefined;
  const displayName = summaryAsset?.name && summaryAsset.name !== summaryAsset.chassis ? summaryAsset.name : undefined;
  const displayPlate = summaryAsset?.licensePlate && summaryAsset.licensePlate !== summaryAsset.chassis ? summaryAsset.licensePlate : undefined;
  
  /**
   * Extrai o número do título (ex: "1º" -> 1, "37º" -> 37)
   */
  const extractNumber = (title: string): number => {
    const match = title.match(/\d+/);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  /**
   * Ordena os agendamentos com os 3 principais sempre em primeiro
   * Os outros são ordenados pelo número do título (1º, 2º, ... 37º)
   * @param schedulings Array de Scheduling
   * @returns Array ordenado com os 3 principais em primeiro
   */
  const sortSchedulings = (schedulings: Scheduling[]): Scheduling[] => {
    // Títulos enviados pela API sempre em pt-BR, independente do idioma da UI
    const mainApiNames = ["Entrega técnica", "Revisão de entrega", "Assentamento"];
    const mainApiNamesLower = mainApiNames.map(t => t.toLowerCase());
    
    const main = schedulings.filter(s => mainApiNamesLower.includes((s.checkup?.name || "").toLowerCase()));
    const others = schedulings.filter(s => !mainApiNamesLower.includes((s.checkup?.name || "").toLowerCase()));
    
    const sortedMain = mainApiNames
      .map(type => main.find(s => s.checkup?.name?.toLowerCase() === type.toLowerCase()))
      .filter((item): item is Scheduling => item !== undefined);
    
    const sortedOthers = others.sort((a, b) => {
      const numA = extractNumber(a.checkup?.name || "");
      const numB = extractNumber(b.checkup?.name || "");
      
      return order === "down" ? numA - numB : numB - numA;
    });
    
    return [...sortedMain, ...sortedOthers];
  };

  const allSortedForIndex = sortSchedulings([
    ...(asset?.schedulings?.previous ?? []),
    ...(asset?.schedulings?.next ?? []),
  ]);
  const mainApiNamesLowerForIndex = ["entrega técnica", "revisão de entrega", "assentamento"];
  const chassis = asset?.chassis ?? "";

  const getRevIndexFromName = (name: string): number | undefined => {
    const match = name.match(/(\d+)[ªº°]/);
    if (match) return parseInt(match[1], 10) + 1;
    return undefined;
  };

  const hasRevisionData = (s: Scheduling): boolean => {
    const revIndex = getRevIndexFromName(s.checkup?.name || "");
    if (revIndex === undefined) return false;
    const rev = getRevisionByIndex(chassis, revIndex);
    return !!rev && (rev.km != null || (rev.parts != null && rev.parts.length > 0));
  };
  
  const preBooksSchedulings = allSortedForIndex.filter(s =>
    mainApiNamesLowerForIndex.includes((s.checkup?.name || "").toLowerCase())
  );
  
  const revisionsSchedulings = allSortedForIndex.filter(s =>
    !mainApiNamesLowerForIndex.includes((s.checkup?.name || "").toLowerCase())
  );
  
  const pagedPreBooks = paginateSchedulings(preBooksSchedulings);
  
  const revisionsWithData: Scheduling[] = [];
  const revisionsWithoutData: Scheduling[] = [];
  revisionsSchedulings.forEach(s => {
    if (hasRevisionData(s)) {
      revisionsWithData.push(s);
    } else {
      revisionsWithoutData.push(s);
    }
  });
  
  const pagedRevisionsWithData = (() => {
    const result: Scheduling[][] = [];
    for (let i = 0; i < revisionsWithData.length; i += 2) {
      result.push(revisionsWithData.slice(i, i + 2));
    }
    return result;
  })();
  
  const pagedRevisionsWithoutData = paginateSchedulings(revisionsWithoutData);
  
  const pagedSchedulings = [
    ...pagedPreBooks.map(page => ({ schedulings: page, isRevisionsWithData: false })),
    ...pagedRevisionsWithData.map(page => ({ schedulings: page, isRevisionsWithData: true })),
    ...pagedRevisionsWithoutData.map(page => ({ schedulings: page, isRevisionsWithData: false })),
  ];

  return (
    <div
      className="revision-content px-20 max-w-[85%] mx-auto"
      style={{ top: "-10px" }}
      id="periodic-reviews-pdf"
    >
      {pagedSchedulings.map((pageData, pageIndex) => (
        <div key={pageIndex} className={`h-[1040px] mt-[10px] overflow-hidden`}>
          {/* Header do veículo em todas as páginas */}
          <div className="flex justify-between items-center border-b border-color-light pb-3 mb-4">
            <div className="flex flex-col gap-2">
              <h3 className="flex gap-4 m-0 text-size-16">
                <span className="rioglyph rioglyph-book text-size-16"></span>
                {intl.formatMessage({ id: "intl-msg:periodicReviews.maintenanceAndReviews" })}
              </h3>
              <div className="flex gap-6 flex-wrap">
                <span className="text-size-12 text-color-dark font-semibold">
                  {displayName ?? "-"}
                </span>
                <span className="text-size-12 text-color-dark">
                  <span className="text-color-gray">{intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.chassis" })}&nbsp;</span>{asset?.chassis || "-"}
                </span>
                <span className="text-size-12 text-color-dark">
                  <span className="text-color-gray">{intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.licensePlate" })}&nbsp;</span>{displayPlate ?? "-"}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-4">
              {pagedSchedulings.length > 1 && (
                <span className="text-size-10 text-color-gray whitespace-nowrap">
                  {pageIndex + 1}&nbsp;/&nbsp;{pagedSchedulings.length}
                </span>
              )}
            </div>
          </div>
          {pageIndex === 0 && (
            <div className="mb-3">
              <span className="text-size-12">{intl.formatMessage({ id: "intl-msg:periodicReviews.reviewsByOrder" })}</span>
            </div>
          )}

          {pageData.isRevisionsWithData ? (
            <div className="flex flex-col gap-4 mt-2" style={{ height: '880px' }}>
              {pageData.schedulings.map((scheduleCheckup: Scheduling) => {
                const blockchain = blockchainData(scheduleCheckup.id);
                const revIndex = getRevIndexFromName(scheduleCheckup.checkup?.name || "");
                const revision = revIndex !== undefined ? getRevisionByIndex(chassis, revIndex) : undefined;
                return (
                  <div key={scheduleCheckup.id} className="flex gap-4" style={{ flex: '1 1 0', minHeight: 0 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Card
                        scheduling={scheduleCheckup}
                        isSigned={!!blockchain?.id}
                        finished={scheduleCheckup.isFinished}
                        blockchain={{
                          transactionDate:
                            (typeof blockchain?.reviewDate === "string"
                              ? new Date(blockchain?.reviewDate).toLocaleDateString(intl.locale)
                              : blockchain?.reviewDate?.toLocaleDateString(intl.locale)) ?? "",
                          transactionHash: blockchain?.id ?? "",
                        }}
                        intl={intl}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <DetailsCard
                        revision={revision}
                        userRole={userRole}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-2 grid-rows-2 gap-4 mt-2" style={{ height: '880px' }}>
              {pageData.schedulings.map((scheduleCheckup: Scheduling) => {
                const blockchain = blockchainData(scheduleCheckup.id);
                return (
                  <Card
                    key={scheduleCheckup.id}
                    scheduling={scheduleCheckup}
                    isSigned={!!blockchain?.id}
                    finished={scheduleCheckup.isFinished}
                    blockchain={{
                      transactionDate:
                        (typeof blockchain?.reviewDate === "string"
                          ? new Date(blockchain?.reviewDate).toLocaleDateString(intl.locale)
                          : blockchain?.reviewDate?.toLocaleDateString(intl.locale)) ?? "",
                      transactionHash: blockchain?.id ?? "",
                    }}
                    intl={intl}
                  />
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

interface ICardProps {
  scheduling: Scheduling;
  isSigned?: boolean;
  finished?: boolean;
  blockchain: Blockchain;
  intl: any;
}
const Card = ({ scheduling, isSigned, finished, blockchain, intl }: ICardProps) => {
  return (
    <div className={`border border-width-2 rounded-lg revision-card flex flex-col h-full overflow-hidden`}>
      <div
        className={`flex justify-between items-center p-2 px-3 border-color-light border-b`}
      >
        <p className="p-0 m-0 truncate text-size-12" style={{ padding: '4px 0' }}>
          {scheduling.checkup?.name}
        </p>
        <span
          className={`rioglyph rioglyph-eye-option ${scheduling.checkup?.services?.length
              ? "cursor-pointer text-color-primary"
              : "cursor-not-allowed"
            }`}
        />
      </div>

      <>
        <div
          className={`flex justify-between gap-2 px-3 py-2 border-color-light border-b`}
        >
          <div className="form-group w-full" style={{ marginBottom: 0 }}>
            <label className="text-size-11">{intl.formatMessage({ id: "intl-msg:periodicReviews.osNumber" })}</label>
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling?.os : ""}
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </div>
          <div className={`form-group w-full`} style={{ marginBottom: 0 }}>
            <label className="text-size-11">{intl.formatMessage({ id: "intl-msg:periodicReviews.date" })}</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.date?.toLocaleDateString(intl.locale)
                  : ""
              }
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </div>
        </div>

        <div
          className={`flex justify-between gap-2 px-3 py-2 border-color-light border-b`}
        >
          <div className="form-group w-full" style={{ marginBottom: 0 }}>
            <label className="text-size-11">{intl.formatMessage({ id: "intl-msg:periodicReviews.hourmeter" })}</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.hourmeter?.toLocaleString(intl.locale)
                  : ""
              }
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </div>

          <div className="form-group w-full" style={{ marginBottom: 0 }}>
            <label className="text-size-11">{intl.formatMessage({ id: "intl-msg:periodicReviews.km" })}</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.odometer?.toLocaleString(intl.locale)
                  : ""
              }
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </div>
        </div>

        <div className="form-group flex flex-wrap pl-2 py-1 field-section border-color-light border-b" style={{ marginBottom: 0 }}>
          {[
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.settling" }),
              value: "Assentamento",
            },
            {
              label: "L",
              value: "L",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.mp1" }),
              value: "MP1",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.mp2" }),
              value: "MP2",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.intermediate" }),
              value: "Intermediária"
            },
          ].map((item) => (
            <div
              key={item.value}
              className="text-left mr-3 my-1 flex items-center"
            >
              <div
                className={`w-3 h-3 border border-gray-400 rounded-sm mr-2 flex items-center justify-center ${scheduling.checkup?.name === item.value
                    ? "bg-primary border-color-info"
                    : "bg-white"
                  }`}
                style={{
                  minWidth: "12px",
                  minHeight: "12px",
                }}
              >
                {scheduling.checkup?.name === item.value && (
                  <svg
                    width="8"
                    height="6"
                    viewBox="0 0 8 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 2.5L3 4.5L7 0.5"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span className="text-size-10 text-color-dark">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="form-group flex flex-col justify-between px-3 py-2 field-section flex-1 overflow-hidden" style={{ marginBottom: 0 }}>
          <label className="block w-full text-size-11 text-color-dark">
            {intl.formatMessage({ id: "intl-msg:periodicReviews.dealership" })}
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling.dealership?.name : ""}
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </label>
          <label className="block w-full text-size-11 text-color-dark">
            {intl.formatMessage({ id: "intl-msg:periodicReviews.consultant" })}
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling.consultant : ""}
              className="form-control border border-gray-300 rounded-md text-gray-700 bg-white"
              style={{ height: '30px', padding: '0 8px', fontSize: '12px' }}
            />
          </label>
        </div>

        <div className="flex items-center font-semibold text-color-primary text-teal-600 footer-card p-1 border-color-light border-t" style={{ minHeight: '36px' }}>
          {finished && !isSigned ? (
            <div
              className={`text-size-10 flex items-center gap-8 justify-between w-full pt-5`}
            >
              <span>{intl.formatMessage({ id: "intl-msg:periodicReviews.completed" })}</span>
              <img src="/assets/images/check-icon.svg" alt={intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.checkIconAlt" })} />
            </div>
          ) : isSigned ? (
            <div className="w-full">
              <div
                className={`text-size-10 flex items-center gap-8 justify-between w-full`}
              >
                <span>{intl.formatMessage({ id: "intl-msg:periodicReviews.authenticated" })}</span>
                <img
                  src="/assets/images/blockchain-enabled.svg"
                  alt={intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.blockchainEnabledAlt" })}
                />
              </div>
              <div
                className={`text-size-10 w-full rounded-md bg-[#F0FEFF] p-2 mt-2`}
              >
                <div className="flex items-center justify-between text-size-10">
                  <div>{intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.transactionId" })}</div>
                  <div>{blockchain.transactionDate}</div>
                </div>
                <div
                  className="text-size-10 break-all"
                  style={{ wordBreak: "break-all", maxWidth: 350 }}
                >
                  {blockchain.transactionHash}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`text-size-10 flex items-center gap-8 justify-between w-full text-color-gray pt-5`}
            >
              <span>{intl.formatMessage({ id: "intl-msg:periodicReviews.awaitingAuth" })}</span>
              <img
                src="/assets/images/blockchain-disabled.svg"
                alt={intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.blockchainDisabledAlt" })}
              />
            </div>
          )}
        </div>
      </>
    </div>
  );
};

interface IDetailsCardProps {
  revision?: ChassisRevision;
  userRole?: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP';
}
const DetailsCard = ({ revision, userRole }: IDetailsCardProps) => {
  const intl = useIntl();
  const hasParts = revision?.parts && revision.parts.length > 0;
  return (
    <div className="border border-width-2 rounded-lg revision-card flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between p-2 px-3 border-color-light border-b">
        <p className="p-0 m-0 font-semibold text-size-12" style={{ padding: '4px 0' }}>
          {intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.reviewDetails" })}
        </p>
        {revision?.parts?.[0]?.dealership && (
          <span className="text-size-10 text-color-gray">{revision.parts[0].dealership}</span>
        )}
      </div>
      <div className="flex flex-col px-3 py-2 gap-2 overflow-hidden flex-1">
        {revision ? (
          <>
            <div className="flex gap-6 flex-wrap" style={{ fontSize: '11px' }}>
              {revision.km != null && (
                <span><span className="text-color-gray">{intl.formatMessage({ id: "intl-msg:periodicReviews.km" })} </span>{revision.km.toLocaleString(intl.locale)}</span>
              )}
              {revision.repairDate && (
                <span><span className="text-color-gray">{intl.formatMessage({ id: "intl-msg:periodicReviews.date" })} </span>{revision.repairDate}</span>
              )}
            </div>
            {hasParts ? (
              <table style={{ width: '100%', fontSize: '9px', borderCollapse: 'collapse', marginTop: '4px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    {userRole !== 'MANAGER' && <th style={{ textAlign: 'left', padding: '2px 4px 2px 0' }}>{intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.code" })}</th>}
                    <th style={{ textAlign: 'left', padding: '2px 4px 2px 0' }}>{intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.description" })}</th>
                    <th style={{ textAlign: 'center', padding: '2px 0' }}>{intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.quantity" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {revision.parts!.map((part, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      {userRole !== 'MANAGER' && <td style={{ padding: '1px 4px 1px 0' }}>{part.partNumber}</td>}
                      <td style={{ padding: '1px 4px 1px 0' }}>{part.partName}</td>
                      <td style={{ textAlign: 'center', padding: '1px 0' }}>{part.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <span style={{ fontSize: '11px' }} className="text-color-gray">{intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.noPartsData" })}</span>
            )}
          </>
        ) : (
          <span style={{ fontSize: '11px' }} className="text-color-gray">{intl.formatMessage({ id: "intl-msg:periodicReviews.periodicReviewsPdf.noData" })}</span>
        )}
      </div>
    </div>
  );
};



export default PeriodicReviewsPdf;